import {
    DataFlowDataType, ShortDataFlowType, IntegerDataFlowType, LongDataFlowType, FloatDataFlowType,
    DoubleDataFlowType, DecimalDataFlowType, BooleanDataFlowType, StringDataFlowType, DateDataFlowType,
    TimeStampDataFlowType, ArrayDataFlowType, IDataFlowDataType, NullDataFlowType, AnyDataFlowType, BinaryDataFlowType, ByteDataFlowType, MapDataFlowType
} from './dataflow-datatype';
import { ComplexDataFlowType, ComplexField, ExternalDataFlowType } from './dataflow-complex-datatype';
import { DataFlowExpression } from './dataflowexpression';
import { stringsAreEqualCaseInsensitive } from 'app/shared/utils/string.util';
import { GlobalVariables } from 'app/services/globals';

export class DataFlowTypesUtils {
    public static allNumberTypeOrderedList: DataFlowDataType[] = [
        ShortDataFlowType.TYPE, IntegerDataFlowType.TYPE, LongDataFlowType.TYPE,
        FloatDataFlowType.TYPE, DoubleDataFlowType.TYPE, DecimalDataFlowType.TYPE];
    public static integerTypeOrderedList: DataFlowDataType[] = [ShortDataFlowType.TYPE, IntegerDataFlowType.TYPE, LongDataFlowType.TYPE];
    public static floatTypeOrderedList: DataFlowDataType[] = [FloatDataFlowType.TYPE, DoubleDataFlowType.TYPE];
    public static booleanTypeOrderedList: DataFlowDataType[] = [BooleanDataFlowType.TYPE];
    public static stringTypeOrderedList: DataFlowDataType[] = [StringDataFlowType.TYPE];
    public static timeStampTypeOrderedList: DataFlowDataType[] = [TimeStampDataFlowType.TYPE];
    public static dateTypeOrderedList: DataFlowDataType[] = [DateDataFlowType.TYPE];
    public static dateTimeTypeOrderedList: DataFlowDataType[] = [DateDataFlowType.TYPE, TimeStampDataFlowType.TYPE];
    public static arrayTypeOrderedList: DataFlowDataType[] = [ArrayDataFlowType.TYPE,
    ...[ShortDataFlowType.TYPE, IntegerDataFlowType.TYPE, LongDataFlowType.TYPE, FloatDataFlowType.TYPE, DoubleDataFlowType.TYPE,
    DateDataFlowType.TYPE, TimeStampDataFlowType.TYPE, BooleanDataFlowType.TYPE, StringDataFlowType.TYPE].map((_type) => `${_type}[]` as DataFlowDataType)];

    public static readonly mapDataFlowTypeRegex = new RegExp(/^\[(\w+(?:\(\d+,\d+\))*),((\w|\[|]|(?:\(\d+,\d+\)))+)]$/);
    public static readonly decimalDataFlowTypeRegex = new RegExp(/^decimal\((\d+),(\d+)\)$/);
    public static readonly userDefinedArgumentRegex = new RegExp(/^\b[i][0-9]{1,5}?$|^[i][0-9]{4}[A-Z]?$/);

    public static getWiderType(left: IDataFlowDataType, right: IDataFlowDataType): IDataFlowDataType {
        if (left instanceof NullDataFlowType) {
            return right;
        }
        DataFlowTypesUtils.integerTypeOrderedList.concat(...DataFlowTypesUtils.floatTypeOrderedList, ...DataFlowTypesUtils.dateTimeTypeOrderedList,
            [BooleanDataFlowType.TYPE, StringDataFlowType.TYPE]);
        if (right instanceof NullDataFlowType) {
            return left;
        }
        return this.getWidestType([left, right]);
    }

    public static getWidestType(inputArguments: IDataFlowDataType[]): IDataFlowDataType {
        if (inputArguments.length === 0) {
            return undefined;
        }

        let typelist: DataFlowDataType[] = [];
        if (inputArguments.every((argument) => DataFlowTypesUtils.isIntegerType(argument))) {
            typelist = DataFlowTypesUtils.integerTypeOrderedList;
        } else if (inputArguments.every((argument) => DataFlowTypesUtils.isNumberType(argument))) {
            // Handle decimal type separately
            if (inputArguments.some((argument) => argument && argument.typeString === 'decimal')) {
                return DataFlowTypesUtils.getWidestTypeForDecimal(inputArguments.filter((argument) => argument && argument.typeString === 'decimal'));
            }
            typelist = DataFlowTypesUtils.allNumberTypeOrderedList;
        } else if (inputArguments.every((argument) => DataFlowTypesUtils.isStringType(argument))) {
            typelist = DataFlowTypesUtils.stringTypeOrderedList;
        } else if (inputArguments.every((argument) => DataFlowTypesUtils.isBooleanType(argument))) {
            typelist = DataFlowTypesUtils.booleanTypeOrderedList;
        } else if (inputArguments.every((argument) => DataFlowTypesUtils.isDateTimeType(argument))) {
            typelist = DataFlowTypesUtils.dateTimeTypeOrderedList;
        } else if (inputArguments.every((argument) => DataFlowTypesUtils.isArrayType(argument))) {
            typelist = DataFlowTypesUtils.arrayTypeOrderedList;
        } else if (inputArguments.every((argument) => argument instanceof ComplexDataFlowType)) {
            const complexType = new ComplexDataFlowType();
            for (const inputArgument of inputArguments) {
                const inputArgumentAsComplexType = inputArgument as ComplexDataFlowType;
                for (const field of inputArgumentAsComplexType.fields) {
                    const fieldType = field.dataFlowExpression;
                    const otherField = complexType.fields.find((existingField) => existingField.entityName === field.entityName);
                    if (otherField) {
                        if (otherField.dataFlowExpression.returnType.typeInDsl !== fieldType.returnType.typeInDsl) {
                            return new AnyDataFlowType();
                        }
                    } else {
                        const dataFlowExpression = new DataFlowExpression();
                        dataFlowExpression.expression = field.dataFlowExpression.expression;
                        dataFlowExpression.returnType = this.cloneType(field.dataFlowExpression.returnType);
                        complexType.fields.push(new ComplexField(field.entityName, dataFlowExpression, complexType));
                    }
                }
            }

            return complexType;
        } else {
            return new AnyDataFlowType();
        }

        let widestIndex = 0;
        let returnType = inputArguments[0];
        inputArguments.forEach((argument) => {
            const index = typelist.findIndex((value) => stringsAreEqualCaseInsensitive(value, argument.typeString));
            if (index > widestIndex) {
                widestIndex = index;
                returnType = argument;
            }
        });

        return returnType;
    }

    public static getWidestTypeForDecimal(inputArguments: IDataFlowDataType[]): IDataFlowDataType {
        if (inputArguments.length === 0) {
            return undefined;
        }
        let returnType = inputArguments[0] as DecimalDataFlowType;
        inputArguments.forEach((argument: DecimalDataFlowType) => {
            if (argument.precision > returnType.precision || (argument.precision === returnType.precision && argument.scale > returnType.scale)) {
                returnType = argument;
            }
        });
        return returnType;
    }

    public static isNumberType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.allNumberTypeOrderedList.includes(dataType.typeString);
    }

    public static isIntegerType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.integerTypeOrderedList.includes(dataType.typeString);
    }

    public static isFloatType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.floatTypeOrderedList.includes(dataType.typeString);
    }

    public static isStringType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.stringTypeOrderedList.includes(dataType.typeString);
    }

    public static isBooleanType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.booleanTypeOrderedList.includes(dataType.typeString);
    }

    public static isDecimalType(dataType: IDataFlowDataType): boolean {
        return dataType && dataType.typeString && dataType.typeString === DecimalDataFlowType.TYPE;
    }

    public static isDateType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.dateTypeOrderedList.includes(dataType.typeString);
    }

    public static isTimeStampType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.timeStampTypeOrderedList.includes(dataType.typeString);
    }

    public static isDateTimeType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.dateTimeTypeOrderedList.includes(dataType.typeString);
    }

    public static isArrayType(dataType: IDataFlowDataType): boolean {
        return dataType && DataFlowTypesUtils.arrayTypeOrderedList.includes(dataType.typeString);
    }

    public static isMapType(dataType: IDataFlowDataType): boolean {
        return dataType instanceof MapDataFlowType;
    }

    public static cloneType(dataFlowDataType: IDataFlowDataType): IDataFlowDataType {
        if (!dataFlowDataType) {
            return undefined;
        }
        const targetType = DataFlowTypesUtils.createType(dataFlowDataType.typeString);

        // createType can return undefined
        if (targetType) {
            targetType.format = dataFlowDataType.format;
        }

        if (targetType instanceof ComplexDataFlowType) {
            const sourceTypeAsComplex = dataFlowDataType as ComplexDataFlowType;
            sourceTypeAsComplex.fields.forEach((field) => {
                const dataFlowExpression = new DataFlowExpression();
                dataFlowExpression.expression = field.dataFlowExpression.expression;
                dataFlowExpression.returnType = this.cloneType(field.dataFlowExpression.returnType);
                targetType.fields.push(new ComplexField(field.entityName, dataFlowExpression, targetType));
            });
        } else if (targetType instanceof DecimalDataFlowType) {
            const sourceTypeAsDecimal = dataFlowDataType as DecimalDataFlowType;
            targetType.scale = sourceTypeAsDecimal.scale;
            targetType.precision = sourceTypeAsDecimal.precision;
        } else if (targetType instanceof ArrayDataFlowType) {
            const sourceTypeAsArray = dataFlowDataType as ArrayDataFlowType;
            targetType.arrayType = DataFlowTypesUtils.cloneType(sourceTypeAsArray.arrayType);
        } else if (targetType instanceof MapDataFlowType) {
            const sourceTypeAsMap = dataFlowDataType as MapDataFlowType;
            targetType.mapKeyType = DataFlowTypesUtils.cloneType(sourceTypeAsMap.mapKeyType);
            targetType.mapValueType = DataFlowTypesUtils.cloneType(sourceTypeAsMap.mapValueType);
        }

        return targetType;
    }

    public static createType(dataFlowType: string, ...subTypes: string[]): IDataFlowDataType {
        if (dataFlowType && dataFlowType.endsWith('[]')) {
            subTypes = [dataFlowType.substr(0, dataFlowType.length - 2)];
            dataFlowType = 'array';
        }

        if (DataFlowTypesUtils.mapDataFlowTypeRegex.test(dataFlowType)) {
            const matches = DataFlowTypesUtils.mapDataFlowTypeRegex.exec(dataFlowType);
            subTypes = [matches[1], matches[2]];
            dataFlowType = 'map';
        }

        if (DataFlowTypesUtils.decimalDataFlowTypeRegex.test(dataFlowType)) {
            const matches = DataFlowTypesUtils.decimalDataFlowTypeRegex.exec(dataFlowType);
            subTypes = [matches[1], matches[2]];
            dataFlowType = 'decimal';
        }

        switch (dataFlowType) {
            case ShortDataFlowType.TYPE:
                return new ShortDataFlowType();
            case IntegerDataFlowType.TYPE:
                return new IntegerDataFlowType();
            case LongDataFlowType.TYPE:
                return new LongDataFlowType();
            case FloatDataFlowType.TYPE:
                return new FloatDataFlowType();
            case DoubleDataFlowType.TYPE:
                return new DoubleDataFlowType();
            case StringDataFlowType.TYPE:
                return new StringDataFlowType();
            case BooleanDataFlowType.TYPE:
                return new BooleanDataFlowType();
            case DateDataFlowType.TYPE:
                return new DateDataFlowType();
            case TimeStampDataFlowType.TYPE:
                return new TimeStampDataFlowType();
            case DecimalDataFlowType.TYPE:
                const decimalType = new DecimalDataFlowType();
                if (subTypes.length === 2) {
                    decimalType.precision = Number(subTypes[0]);
                    decimalType.scale = Number(subTypes[1]);
                }
                return decimalType;
            case NullDataFlowType.TYPE:
                return new NullDataFlowType();
            case ArrayDataFlowType.TYPE:
                const subType = subTypes && subTypes[0] || AnyDataFlowType.TYPE;
                return new ArrayDataFlowType(DataFlowTypesUtils.createType(subType));
            case MapDataFlowType.TYPE:
                return new MapDataFlowType(DataFlowTypesUtils.createType(subTypes[0]), DataFlowTypesUtils.createType(subTypes[1]));
            case AnyDataFlowType.TYPE:
                return new AnyDataFlowType();
            case ComplexDataFlowType.TYPE:
                return new ComplexDataFlowType();
            case BinaryDataFlowType.TYPE:
                return new BinaryDataFlowType();
            case ByteDataFlowType.TYPE:
                return new ByteDataFlowType();
            case ExternalDataFlowType.TYPE:
                return new ExternalDataFlowType();
            default:
                return undefined;
        }
    }

    public static typeMatch(actualType: DataFlowDataType, expectedType: DataFlowDataType): boolean {
        const actualDataFlowType = DataFlowTypesUtils.createType(actualType);
        const expectedDataFlowType = DataFlowTypesUtils.createType(expectedType);

        if (!actualDataFlowType) {
            return false;
        }

        // Using wide type method to compare real data type instead of super type (i.e. number)
        if (DataFlowTypesUtils.getWidestType([actualDataFlowType, expectedDataFlowType]).typeString !== 'any') {
            return true;
        }

        switch (expectedType) {
            case 'number':
                return DataFlowTypesUtils.isNumberType(actualDataFlowType);
            case 'integral':
                return DataFlowTypesUtils.isIntegerType(actualDataFlowType);
            case 'fractional':
                return DataFlowTypesUtils.isFloatType(actualDataFlowType) || actualType === 'decimal';
            case 'datetime':
                return DataFlowTypesUtils.isDateTimeType(actualDataFlowType);
            default:
                return actualType === expectedType;
        }
    }

    public static parameterTypeMatch(actualType: IDataFlowDataType, expectedType: string): boolean {
        if (GlobalVariables.enableParameterUndefinedValidation && expectedType === '') {
            return false;
        }
        const fullActualType = DataFlowTypesUtils.getFullParameterType(actualType).replace(new RegExp(/decimal\((\d+),(\d+)\)/), 'decimal') as DataFlowDataType;

        return DataFlowTypesUtils.createType(expectedType).compatibleTypes.includes(fullActualType);
    }

    public static getFullParameterType(dataflowDataType: IDataFlowDataType): DataFlowDataType {
        return dataflowDataType.fullyQualifiedType;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static commonFormats(datatype: string): any {
        switch ((datatype || '').toLowerCase()) {
            case 'date':
            case 'datetime':
            case 'datetimeoffset':
                return {
                    options: [
                        'MM/dd/yyyy',
                        'dd/MM/yyyy',
                        'yyyy/MM/dd',
                        'MM-dd-yyyy',
                        'dd-MM-yyyy',
                        'yyyy-MM-dd',
                        'dd.MM.yyyy',
                        'MM.dd.yyyy',
                        'yyyy.MM.dd'
                    ],
                    required: false
                };
            case 'timestamp':
                return {
                    options: [
                        'MM/dd/yyyy',
                        'dd/MM/yyyy',
                        'yyyy/MM/dd',
                        'MM/dd/yyyy HH:mm:ss',
                        'MM/dd/yyyy hh:mm:ss a',
                        'MM-dd-yyyy',
                        'dd-MM-yyyy',
                        'yyyy-MM-dd',
                        'MM-dd-yyyy HH:mm:ss',
                        'MM-dd-yyyy hh:mm:ss a',
                        'dd.MM.yyyy',
                        'MM.dd.yyyy',
                        'yyyy.MM.dd',
                        'yyyy.MM.dd HH:mm:ss',
                        'yyyy.MM.dd hh:mm:ss a',
                        'yyyy-MM-dd\'T\'HH:mm:ss',
                        'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'',
                        'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\''
                    ],
                    required: false
                };
            case 'integer':
            case 'int32':
            case 'short':
            case 'int16':
                return {
                    options: [
                        '000',
                        '##0',
                        '$###',
                        '###%',
                        '###,###',
                        '$###,###',
                        '###%,###'
                    ],
                    required: false
                };
            case 'long':
            case 'int64':
                return {
                    options: [
                        '000,000,000',
                        '###,###,000',
                        '$#########',
                        '#########%',
                        '$###,###,###',
                        '###,###,###%',
                        '###,###,###,###'
                    ],
                    required: false
                };
            case 'float':
            case 'single':
                return {
                    options: [
                        '000.##',
                        '##0.##',
                        '###.##',
                        '$###.##',
                        '###.##%',
                        '###,###.##',
                        '$###,###.##',
                        '###,###.##%'
                    ],
                    required: false
                };
            case 'double':
            case 'decimal':
                return {
                    options: [
                        '000,000,000.000',
                        '###,###,000.###',
                        '$#########.###',
                        '#########.###%',
                        '$###,###,###.###',
                        '###,###,###.###%',
                        '###,###,###,###.###'
                    ],
                    required: false
                };
            default:
                return null;
        }
    }

    public static isUserDefinedFunctionArgumentPattern(columnName: string): boolean {
        return columnName && DataFlowTypesUtils.userDefinedArgumentRegex.test(columnName);
    }
}
